import React from 'react';
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';

import { CircularProgress } from '@src/components/CircularProgress';
import { getTestId } from '@src/utils/testIds';
import { InternalButtonIcon, InternalButtonProps, InternalButtonVariant } from './interface';
import { getVariantColors, iconSizeLookup } from './utils';
import { Icon } from '@src/components/Icon';
import { Tooltip } from '@mui/material';

export const InternalButton = (props: InternalButtonProps) => {
  const muiProps = medelyPropsToMuiProps(props);

  if (props.tooltip) {
    return (
      <Tooltip title={props.tooltip}>
        <span>
          <MuiButton
            {...muiProps}
            fullWidth={props.fullWidth}
            data-testid={getTestId(props)}
            disableElevation
          >
            {props.children}
          </MuiButton>
        </span>
      </Tooltip>
    )
  }
  return (
    <MuiButton
      {...muiProps}
      fullWidth={props.fullWidth}
      data-testid={getTestId(props)}
      disableElevation
    >
      {props.children}
    </MuiButton>
  );
};

const medelyPropsToMuiProps = (medelyProps: InternalButtonProps): MuiButtonProps => {
  const { disabled, type, onClick } = medelyProps;
  const sx = buildSx(medelyProps);
  const variant = medelyProps.variant ?? 'contained';

  const onClickHandler = () => {
    if (medelyProps.loading) {
      return;
    }

    onClick?.();
  };

  const startIcon = getIcon(medelyProps.startIcon);
  const endIcon = medelyProps.loading ? (
    <CircularProgress size={16} sx={{ color: 'inherit' }} />
  ) : (
    getIcon(medelyProps.endIcon)
  );

  return {
    variant,
    startIcon,
    endIcon,
    onClick: onClickHandler,
    disabled,
    type,
    sx
  };
};

const buildSx = (medelyProps: InternalButtonProps): MuiButtonProps['sx'] => {
  const disabled = medelyProps.disabled ?? false;
  const variant = medelyProps.variant ?? 'contained';
  const color = medelyProps.color ?? 'primary';

  const variantSX = getVariantSX(variant, color, disabled);
  const shapeSX = medelyProps.shape === 'square' ? { borderRadius: '8px' } : {};

  const p = medelyProps.isIconOnly ? '4px 4px 4px 16px' : '4px 16px';
  const minWidth = medelyProps.isIconOnly ? '40px' : '64px';
  const minHeight = medelyProps.isIconOnly ? '40px' : 'auto';
  const width = medelyProps.isIconOnly ? '40px' : 'auto';

  return {
    p,
    minWidth,
    width,
    minHeight,
    ...variantSX,
    ...shapeSX
  }
};

function getIcon(icon?: InternalButtonIcon, color?: string) {
  if (!icon) {
    return null;
  }
  else if (typeof icon === 'string') {
    const size = iconSizeLookup[icon] || "18px";
    return <Icon name={icon} boxSize={size} iconSize={size} variant="custom" color={color} />;
  }
  else {
    return icon;
  }
}

function getVariantSX(variant: InternalButtonVariant, color: string, disabled: boolean): MuiButtonProps['sx'] {
  const variantColors = getVariantColors(variant, color);

  return {
    backgroundColor: variantColors.backgroundColor,
    color: variantColors.textColor,
    borderWidth: variantColors.borderWidth ?? '2px',
    borderStyle: 'solid',
    borderColor: variantColors.borderColor || 'transparent',
    boxSizing: 'border-box',
    '&:hover': {
      backgroundColor: variantColors.hoverBackgroundColor,
    },
    '&:active': {
      backgroundColor: variantColors.pressedBackgroundColor,
    },
    '&:focus': {
      borderColor: variantColors.focusedBorderColor,
      borderWidth: variantColors.borderWidth ?? '2px',
    },
    '&.Mui-disabled': {
      color: variantColors.disabledTextColor,
      backgroundColor: variantColors.disabledBackgroundColor
    }
  };
}
